import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { supabase } from '../../lib/supabaseClient';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons
const providerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxOCIgZmlsbD0iIzE0QjhBNiIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSI0Ii8+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iOCIgZmlsbD0id2hpdGUiLz48L3N2Zz4=',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

const customerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxOCIgZmlsbD0iI0ZGNkEwMCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSI0Ii8+PHBhdGggZD0iTTIwIDEwVjMwTTEwIDIwSDMwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPjwvc3ZnPg==',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

interface LeafletLiveTrackerProps {
  jobId: string;
  providerId: string;
  customerLatitude: number;
  customerLongitude: number;
  customerAddress: string;
  isProvider?: boolean;
  jobStatus: string;
}

interface LocationData {
  latitude: number;
  longitude: number;
  updated_at: string;
}

// Component to auto-fit bounds
function MapBounds({ 
  providerLat, 
  providerLng, 
  customerLat, 
  customerLng 
}: { 
  providerLat: number; 
  providerLng: number; 
  customerLat: number; 
  customerLng: number; 
}) {
  const map = useMap();

  useEffect(() => {
    const bounds = L.latLngBounds(
      [providerLat, providerLng],
      [customerLat, customerLng]
    );
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [map, providerLat, providerLng, customerLat, customerLng]);

  return null;
}

export default function LeafletLiveTracker({
  jobId,
  providerId,
  customerLatitude,
  customerLongitude,
  customerAddress,
  isProvider = false,
  jobStatus
}: LeafletLiveTrackerProps) {
  const [providerLocation, setProviderLocation] = useState<LocationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tracking, setTracking] = useState(false);
  const [lastUpdateText, setLastUpdateText] = useState('');
  const watchIdRef = useRef<number | null>(null);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const locationUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate "last updated" text
  useEffect(() => {
    if (!providerLocation) return;

    const updateLastUpdateText = () => {
      const lastUpdated = new Date(providerLocation.updated_at);
      const secondsAgo = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);

      if (secondsAgo < 10) {
        setLastUpdateText('Just now');
      } else if (secondsAgo < 60) {
        setLastUpdateText(`${secondsAgo} seconds ago`);
      } else {
        const minutesAgo = Math.floor(secondsAgo / 60);
        setLastUpdateText(`${minutesAgo} minute${minutesAgo > 1 ? 's' : ''} ago`);
      }
    };

    updateLastUpdateText();
    const interval = setInterval(updateLastUpdateText, 1000);

    return () => clearInterval(interval);
  }, [providerLocation]);

  // Provider side: Start GPS tracking
  useEffect(() => {
    if (isProvider && (jobStatus === 'accepted' || jobStatus === 'in_progress')) {
      startLocationTracking();
    } else if (isProvider) {
      stopLocationTracking();
    }

    return () => {
      stopLocationTracking();
    };
  }, [isProvider, jobStatus]);

  // Customer side: Fetch provider location
  useEffect(() => {
    if (!isProvider) {
      fetchProviderLocation();

      // Poll every 10 seconds
      updateIntervalRef.current = setInterval(() => {
        fetchProviderLocation();
      }, 10000);
    }

    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, [isProvider, jobId]);

  const startLocationTracking = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setLoading(false);
      return;
    }

    try {
      // Request permission
      const permission = await navigator.permissions.query({ name: 'geolocation' as const });

      if (permission.state === 'denied') {
        setError('Location permission denied. Please enable location access in your browser settings.');
        setLoading(false);
        return;
      }

      setTracking(true);
      setLoading(false);
      setError('');

      // Start watching position
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          sendLocationUpdate(latitude, longitude);
        },
        (err) => {
          console.error('Geolocation error:', err);
          let errorMessage = 'Unable to get your location. ';
          
          switch (err.code) {
            case err.PERMISSION_DENIED:
              errorMessage += 'Please enable location access.';
              break;
            case err.POSITION_UNAVAILABLE:
              errorMessage += 'Location information unavailable.';
              break;
            case err.TIMEOUT:
              errorMessage += 'Location request timed out.';
              break;
            default:
              errorMessage += 'Please check your settings.';
          }
          
          setError(errorMessage);
          setTracking(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );

      // Also send updates every 15 seconds as backup
      locationUpdateIntervalRef.current = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            sendLocationUpdate(latitude, longitude);
          },
          (err) => {
            console.error('Backup location update error:', err);
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      }, 15000);

    } catch (err) {
      console.error('Error starting location tracking:', err);
      setError('Failed to start location tracking. Please refresh and try again.');
      setLoading(false);
    }
  };

  const sendLocationUpdate = async (latitude: number, longitude: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Update job_live_locations table
      const { error: dbError } = await supabase
        .from('job_live_locations')
        .upsert({
          job_id: jobId,
          provider_id: providerId,
          latitude,
          longitude,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'job_id,provider_id'
        });

      if (dbError) {
        console.error('Error updating location in DB:', dbError);
      }

    } catch (err) {
      console.error('Error sending location update:', err);
    }
  };

  const stopLocationTracking = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (locationUpdateIntervalRef.current) {
      clearInterval(locationUpdateIntervalRef.current);
      locationUpdateIntervalRef.current = null;
    }
    setTracking(false);
  };

  const fetchProviderLocation = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('job_live_locations')
        .select('latitude, longitude, updated_at')
        .eq('job_id', jobId)
        .eq('provider_id', providerId)
        .single();

      if (fetchError) {
        if (fetchError.code !== 'PGRST116') {
          console.error('Error fetching location:', fetchError);
        }
        setProviderLocation(null);
      } else if (data) {
        setProviderLocation(data);
        setError('');
      }
    } catch (err) {
      console.error('Error fetching provider location:', err);
    } finally {
      setLoading(false);
    }
  };

  // Provider view
  if (isProvider) {
    return (
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Live Location Tracking</h3>
          {tracking && (
            <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs sm:text-sm font-semibold">Active</span>
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line text-red-600 text-lg flex-shrink-0 mt-0.5"></i>
              <p className="text-xs sm:text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {!error && tracking && (
          <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-3">
              <i className="ri-map-pin-line text-xl sm:text-2xl text-green-600 flex-shrink-0"></i>
              <div>
                <p className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Your location is being shared</p>
                <p className="text-xs sm:text-sm text-gray-600">
                  The customer can see your location in real-time while you're on the way.
                </p>
              </div>
            </div>
          </div>
        )}

        {!tracking && !error && loading && (
          <div className="text-center py-4">
            <i className="ri-loader-4-line text-2xl sm:text-3xl text-gray-400 animate-spin"></i>
            <p className="text-xs sm:text-sm text-gray-600 mt-2">Starting location tracking...</p>
          </div>
        )}

        {!tracking && !error && !loading && (
          <div className="p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-xl sm:text-2xl text-gray-500 flex-shrink-0"></i>
              <div>
                <p className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Tracking paused</p>
                <p className="text-xs sm:text-sm text-gray-600">
                  Location tracking is only active when job status is "On Route" or "In Progress".
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Customer view
  if (loading) {
    return (
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-3"></div>
          <p className="text-sm sm:text-base text-gray-600">Loading location...</p>
        </div>
      </div>
    );
  }

  if (!providerLocation) {
    return (
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
        <div className="text-center py-8">
          <i className="ri-map-pin-off-line text-4xl sm:text-5xl text-gray-300 mb-4"></i>
          <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2">Location Not Available</h3>
          <p className="text-xs sm:text-sm text-gray-600">
            Provider location tracking will appear when they're on the way to your location.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
              <i className="ri-map-pin-line text-teal-600"></i>
              Live Location Tracking
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Last updated: {lastUpdateText}
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-green-100 text-green-700 rounded-full self-start sm:self-auto">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs sm:text-sm font-semibold">Live</span>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="w-full h-64 sm:h-96">
          <MapContainer
            center={[
              (providerLocation.latitude + customerLatitude) / 2,
              (providerLocation.longitude + customerLongitude) / 2
            ]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Provider Marker */}
            <Marker 
              position={[providerLocation.latitude, providerLocation.longitude]}
              icon={providerIcon}
            >
              <Popup>
                <div className="text-center">
                  <p className="font-semibold text-teal-600">Provider Location</p>
                  <p className="text-xs text-gray-600 mt-1">On the way to you</p>
                </div>
              </Popup>
            </Marker>

            {/* Customer Marker */}
            <Marker 
              position={[customerLatitude, customerLongitude]}
              icon={customerIcon}
            >
              <Popup>
                <div className="text-center">
                  <p className="font-semibold text-orange-600">Your Location</p>
                  <p className="text-xs text-gray-600 mt-1">{customerAddress}</p>
                </div>
              </Popup>
            </Marker>

            {/* Auto-fit bounds */}
            <MapBounds
              providerLat={providerLocation.latitude}
              providerLng={providerLocation.longitude}
              customerLat={customerLatitude}
              customerLng={customerLongitude}
            />
          </MapContainer>
        </div>

        <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-3 sm:p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-car-line text-teal-600 text-lg sm:text-xl"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Provider is on the way</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Heading to: {customerAddress}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 bg-gray-50">
        <div className="flex items-start gap-2 text-xs sm:text-sm text-gray-600">
          <i className="ri-information-line flex-shrink-0 mt-0.5"></i>
          <p>Location updates automatically every 10-15 seconds while provider is en route. Map shows real-time position using OpenStreetMap.</p>
        </div>
      </div>
    </div>
  );
}
